import { useCreateAsset, useCreateDirectUpload } from '@lopo-tech/lopo-api'
import CryptoJS from 'crypto-js'

async function generateMD5Base64 (blob: Blob): Promise<string> {
  // Convert blob to ArrayBuffer
  const arrayBuffer = await blob.arrayBuffer()

  // Convert ArrayBuffer to WordArray for crypto-js
  const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer)

  // Generate MD5 hash
  const md5Hash = CryptoJS.MD5(wordArray)

  // Convert to base64
  const base64Hash = md5Hash.toString(CryptoJS.enc.Base64)

  return base64Hash
}

type DirectUploadResponse = {
  url?: string;
  headers: Record<string, string>;
  blobId?: string;
};

const fetchBlob = async (
  imageUrl: string
): Promise<{ blob: Blob; filename: string }> => {
  const res = await fetch(imageUrl)
  if (!res.ok) { throw new Error(`Failed to fetch image ${imageUrl}: ${res.status}`) }
  const blob = await res.blob()
  const filename = (imageUrl.split('/').pop() || 'upload').split('?')[0]
  return { blob, filename }
}

const requestDirectUpload = async (
  createDirectUpload: ReturnType<typeof useCreateDirectUpload>[0],
  input: {
    byteSize: number;
    checksum: string;
    contentType: string;
    filename: string;
  }
): Promise<DirectUploadResponse> => {
  // createDirectUpload has the GraphQL signature; call it with variables
  const res = await createDirectUpload({ variables: { input } })
  // try a few common shapes for the response
  const directUpload = res?.data?.createDirectUpload?.directUpload
  const url = directUpload?.url
  const headers = directUpload?.headers ? JSON.parse(directUpload.headers) : {}
  const signedBlobId = directUpload?.signedBlobId

  if (!url && !signedBlobId) { throw new Error('createDirectUpload returned no usable url or signedBlobId') }

  return { url, headers, blobId: signedBlobId }
}

const uploadToUrl = async (
  uploadUrl: string,
  headers: HeadersInit | undefined,
  blob: Blob
) => {
  const fetchOptions: RequestInit = {
    method: 'PUT',
    body: blob,
    headers: { ...headers }
  }
  const res = await fetch(uploadUrl, fetchOptions)
  if (!res.ok) throw new Error(`Upload to storage failed: ${res.status}`)
  return res
}

// Factory for blob upload only (without asset creation)
export const useCreateBlobFlow = () => {
  const [createDirectUpload] = useCreateDirectUpload()

  const uploadOne = async (imageUrl: string): Promise<string | undefined> => {
    try {
      const { blob, filename } = await fetchBlob(imageUrl)
      const byteSize = blob.size
      const contentType = blob.type || 'application/octet-stream'
      const checksum = await generateMD5Base64(blob)

      const direct = await requestDirectUpload(createDirectUpload, {
        byteSize,
        checksum,
        contentType,
        filename
      })
      const uploadUrl = direct.url
      const uploadHeaders = direct.headers
      const blobId = direct.blobId

      if (!uploadUrl || !blobId) { throw new Error('Direct upload response malformed') }

      await uploadToUrl(uploadUrl, uploadHeaders, blob)

      // Return blob ID instead of creating asset
      return blobId
    } catch (e) {
      // don't crash the whole batch; caller can decide how to handle nulls
      // eslint-disable-next-line no-console
      console.error('uploadOne failed for', imageUrl, e)
      return undefined
    }
  }

  return async (images: string[] = []): Promise<string[]> => {
    const results = await Promise.all(images.map((img) => uploadOne(img)))
    return results.filter((x) => x != null)
  }
}

// Factory keeping original signature: returns an async function that uploads images
export const useCreateAssetFlow = () => {
  const [createAsset] = useCreateAsset()
  const [createDirectUpload] = useCreateDirectUpload()

  const uploadOne = async (imageUrl: string): Promise<string | undefined> => {
    try {
      const { blob, filename } = await fetchBlob(imageUrl)
      const byteSize = blob.size
      const contentType = blob.type || 'application/octet-stream'
      const checksum = await generateMD5Base64(blob)

      const direct = await requestDirectUpload(createDirectUpload, {
        byteSize,
        checksum,
        contentType,
        filename
      })
      const uploadUrl = direct.url
      const uploadHeaders = direct.headers
      const blobId = direct.blobId

      if (!uploadUrl || !blobId) { throw new Error('Direct upload response malformed') }

      await uploadToUrl(uploadUrl, uploadHeaders, blob)

      const asset = await createAsset({
        variables: {
          input: {
            altText: '',
            blobId,
            name: filename
          }
        }
      })

      const assetId = asset.data?.createAsset?.asset.id
      return assetId
    } catch (e) {
      // don't crash the whole batch; caller can decide how to handle nulls
      // eslint-disable-next-line no-console
      console.error('uploadOne failed for', imageUrl, e)
      return undefined
    }
  }

  return async (images: string[] = []): Promise<string[]> => {
    const results = await Promise.all(images.map((img) => uploadOne(img)))
    return results.filter((x) => x != null)
  }
}
